import { useRef, useMemo, useEffect } from 'react';

interface CountSnapshot {
  timestamp: number;
  count: number;
  weight: number;
}

interface SlidingWindowRateOptions {
  windowSizeSeconds?: number; // default: 3
}

interface SlidingWindowRateResult {
  fishPerHour: number;
  tonnesPerHour: number;
}

/**
 * Calculates fish/hour and tonnes/hour using a sliding window approach.
 *
 * - Before windowSizeSeconds: calculates based on actual elapsed time
 * - After windowSizeSeconds: uses sliding window of last N seconds
 * - When not counting: returns 0
 *
 * @param currentCount - Current total fish count
 * @param currentWeight - Current total weight in tonnes
 * @param isCounting - Whether counting is active
 * @param options - Configuration options
 */
export function useSlidingWindowRate(
  currentCount: number,
  currentWeight: number,
  isCounting: boolean,
  options?: SlidingWindowRateOptions
): SlidingWindowRateResult {
  const windowSizeSeconds = options?.windowSizeSeconds ?? 3;
  const windowSizeMs = windowSizeSeconds * 1000;

  // History of count/weight snapshots
  const historyRef = useRef<CountSnapshot[]>([]);
  // Track previous count to detect changes
  const prevCountRef = useRef<number | null>(null);

  // Reset history when counting stops
  useEffect(() => {
    if (!isCounting) {
      historyRef.current = [];
      prevCountRef.current = null;
    }
  }, [isCounting]);

  // Calculate rates using sliding window
  const rates = useMemo(() => {
    if (!isCounting) {
      return { fishPerHour: 0, tonnesPerHour: 0 };
    }

    const now = Date.now();

    // Add new snapshot when count changes
    if (prevCountRef.current !== currentCount) {
      historyRef.current.push({
        timestamp: now,
        count: currentCount,
        weight: currentWeight,
      });
      prevCountRef.current = currentCount;
    }

    // Remove snapshots older than window size
    const cutoffTime = now - windowSizeMs;
    historyRef.current = historyRef.current.filter((s) => s.timestamp >= cutoffTime);

    const history = historyRef.current;

    // Need at least 2 snapshots to calculate rate
    if (history.length < 2) {
      return { fishPerHour: 0, tonnesPerHour: 0 };
    }

    // Get first and last snapshots
    const firstSnapshot = history[0];
    const lastSnapshot = history[history.length - 1];

    // Calculate elapsed time in hours
    const elapsedMs = lastSnapshot.timestamp - firstSnapshot.timestamp;
    if (elapsedMs <= 0) {
      return { fishPerHour: 0, tonnesPerHour: 0 };
    }

    const elapsedHours = elapsedMs / (1000 * 60 * 60);

    // Calculate delta count and weight
    const deltaCount = lastSnapshot.count - firstSnapshot.count;
    const deltaWeight = lastSnapshot.weight - firstSnapshot.weight;

    // Calculate rates
    const fishPerHour = deltaCount / elapsedHours;
    const tonnesPerHour = deltaWeight / elapsedHours;

    return {
      fishPerHour: Math.max(0, fishPerHour),
      tonnesPerHour: Math.max(0, tonnesPerHour),
    };
  }, [isCounting, currentCount, currentWeight, windowSizeMs]);

  return rates;
}
